import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { initAnalytics, revokeAnalytics } from "@/lib/analytics";

const KEY = "momentum.cookie-consent";

export type ConsentValue = "all" | "essential";

export function getStoredConsent(): ConsentValue | null {
  if (typeof window === "undefined") return null;
  const v = localStorage.getItem(KEY);
  return v === "all" || v === "essential" ? v : null;
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!getStoredConsent()) setVisible(true);
    const open = () => setVisible(true);
    window.addEventListener("momentum:open-cookie-settings", open);
    return () => window.removeEventListener("momentum:open-cookie-settings", open);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (!visible || !ref.current) {
      root.style.removeProperty("--consent-offset");
      return;
    }
    const el = ref.current;
    const sync = () => root.style.setProperty("--consent-offset", `${el.offsetHeight}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--consent-offset");
    };
  }, [visible]);

  const choose = (value: ConsentValue) => {
    localStorage.setItem(KEY, value);
    localStorage.setItem(`${KEY}.at`, new Date().toISOString());
    window.dispatchEvent(new CustomEvent("momentum:cookie-consent", { detail: value }));
    if (value === "all") initAnalytics();
    else revokeAnalytics();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-title"
      className="fixed inset-x-0 bottom-0 z-[70] border-t border-border bg-card/95 backdrop-blur-md"
    >

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-5 py-5 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-md">
          <p id="cookie-title" className="micro-label">
            Cookies
          </p>
          <p className="mt-2 text-xs leading-snug text-muted-foreground">
            Essential cookies keep you signed in. Analytics is optional.{" "}
            <Link to="/legal" hash="privacy" className="text-foreground underline underline-offset-4">
              Privacy policy
            </Link>
            .
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-3">
          <button
            type="button"
            onClick={() => choose("essential")}
            className="min-h-11 rounded-sm border border-border px-5 text-sm font-medium transition-colors hover:border-foreground/40"
          >
            Essential only
          </button>
          <button
            type="button"
            onClick={() => choose("all")}
            className="min-h-11 rounded-sm bg-primary px-5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
